/**
 * Checklist « site opérationnel » — règle pure, sans Angular ni HTTP (P1 #3, 2026-10-03).
 *
 * <p>Prototype validé : `docs/maquettes/mise-en-service/prototype.html`, écrans 1 à 3. La checklist
 * est INFORMATIVE : elle ne bloque aucun geste, elle dit où en est le site.
 *
 * <p>La règle qui prime : une ligne n'est verte que si une donnée la prouve.
 * <ul>
 *   <li>Ce que le terrain n'accuse pas a son propre état : la config validée est « envoyée », sans
 *       accusé ({@code noack}) ; la RF ({@code isEmitting}) est une intention ({@code intent}).</li>
 *   <li>Une donnée qui n'a pas pu être chargée donne {@code unknown} (« inconnu (erreur de
 *       chargement) »), jamais « fait » ni « à faire ».</li>
 * </ul>
 *
 * <p>Distinct de {@link computeSiteHealth} (le site marche-t-il maintenant ?) et de
 * {@code SiteConfigurationService.readiness} (le brouillon de l'onglet Configuration peut-il être
 * validé ?) : ici, ce qui est enregistré côté serveur, étape par étape.
 *
 * <p>Ni LoRa, ni firmware de site, ni champs de correction (décision d'Eddy du 2026-10-08) : ce
 * n'est pas obligatoire pour qu'un site soit opérationnel. Ces étapes restent dans leurs onglets.
 *
 * <p>Vit dans domain-sites (scope shared) pour que l'integrator puisse la reprendre ; seul inside
 * la consomme pour l'instant.
 */

/** Une source de donnée : en cours, en échec, ou chargée. */
export type Loadable<T> =
  | { readonly status: 'loading' }
  | { readonly status: 'error' }
  | { readonly status: 'loaded'; readonly value: T };

export const loading = <T>(): Loadable<T> => ({ status: 'loading' });
export const loadError = <T>(): Loadable<T> => ({ status: 'error' });
export const loaded = <T>(value: T): Loadable<T> => ({ status: 'loaded', value });

/**
 * Statut de configuration du site (`Site.status` de sitemanager). Décision du 2026-10-07 : plus
 * de circuit de demande de validation, un site est DRAFT (aucun plan enregistré) ou VALIDATED.
 */
export type SiteConfigStatus = 'DRAFT' | 'VALIDATED';

export interface ChecklistPosition {
  latitude?: number | null;
  longitude?: number | null;
  altitude?: number | null;
}

export interface ChecklistGateway {
  id: string;
  name: string;
  /** Intention enregistrée (bascule RF), pas une mesure de l'émission. */
  isEmitting: boolean | undefined;
  /** Position propre de la gateway (sert d'antenne implicite quand aucune n'est persistée). */
  position: ChecklistPosition;
  /** Antennes persistées. Vide = une antenne implicite à la position de la gateway. */
  antennas: ChecklistPosition[];
}

/** ADR-039 : fichier de fréquences JSON (`frequency_file`) + calib. */
export interface ChecklistFiles {
  /** Fichier de fréquences (freq, freq_delta, theta) présent. */
  freq: boolean;
  calib: boolean;
  /** Le site a un preset actif (`activePresetId`). */
  fromPreset: boolean;
  /** Nom du preset actif, lu dans le catalogue ; null = introuvable (erreur ou preset absent). */
  presetName?: string | null;
}

export interface SiteChecklistInput {
  configStatus: Loadable<SiteConfigStatus | null>;
  gateways: Loadable<ChecklistGateway[]>;
  files: Loadable<ChecklistFiles>;
  /** Nombre de trackers assignés au site. */
  trackers: Loadable<number>;
}

/**
 * <ul>
 *   <li>{@code ok} — vérifié par une donnée.</li>
 *   <li>{@code partial} — commencé, incomplet.</li>
 *   <li>{@code todo} — à faire.</li>
 *   <li>{@code noack} — envoyé, sans accusé du terrain : ne peut pas passer au vert.</li>
 *   <li>{@code intent} — intention enregistrée, sans mesure : ne peut pas passer au vert.</li>
 *   <li>{@code later} — attend une étape précédente.</li>
 *   <li>{@code recommended} — ligne facultative non faite.</li>
 *   <li>{@code unknown} — la donnée manque (erreur de chargement ou valeur absente).</li>
 *   <li>{@code loading} — la donnée arrive.</li>
 * </ul>
 */
export type ChecklistState =
  | 'ok'
  | 'partial'
  | 'todo'
  | 'noack'
  | 'intent'
  | 'later'
  | 'recommended'
  | 'unknown'
  | 'loading';

export type ChecklistGroup = 'Matériel' | 'Configuration radio' | 'Trackers';

/** Onglet de la fiche site où se fait l'étape (chemin relatif de la route). */
export type ChecklistTab = 'config' | 'gateways' | 'trackers';

export interface ChecklistRow {
  id: string;
  group: ChecklistGroup;
  label: string;
  state: ChecklistState;
  detail: string;
  /** D'où vient la donnée — affiché sous la ligne. */
  source: string;
  tab: ChecklistTab;
  optional: boolean;
}

export type ChecklistHeadline = 'not-ready' | 'almost' | 'incomplete' | 'loading' | 'reserve';

export interface ChecklistSummary {
  /** Lignes obligatoires. */
  total: number;
  /** Vérifiées + non vérifiables : ce qu'inside a pu mener jusqu'au bout. */
  passed: number;
  verified: number;
  unverifiable: number;
  /** À faire ou partielles. */
  todo: number;
  later: number;
  unknown: number;
  loading: number;
  /** Première ligne obligatoire à faire ; null quand il n'y en a plus. */
  next: ChecklistRow | null;
  headline: ChecklistHeadline;
}

const LOAD_ERROR = 'inconnu (erreur de chargement)';
const LOADING = 'chargement…';

const plural = (n: number, one: string, many = one + 's') => `${n} ${n > 1 ? many : one}`;

const isFiniteNumber = (v: number | null | undefined): v is number => typeof v === 'number' && Number.isFinite(v);
const isPositioned = (p: ChecklistPosition) =>
  isFiniteNumber(p.latitude) && isFiniteNumber(p.longitude) && isFiniteNumber(p.altitude);

/** Une ligne dont la source n'est pas (encore) disponible. */
function unavailable<T>(source: Loadable<T>): { state: ChecklistState; detail: string } | null {
  if (source.status === 'error') return { state: 'unknown', detail: LOAD_ERROR };
  if (source.status === 'loading') return { state: 'loading', detail: LOADING };
  return null;
}

/** Même règle que SiteConfigurationService.seedAntennasForGateway : sans antenne, une implicite. */
const antennasOf = (g: ChecklistGateway): ChecklistPosition[] => (g.antennas.length ? g.antennas : [g.position]);

const STATUS_DETAIL: Record<SiteConfigStatus, string> = {
  DRAFT: 'DRAFT · Validate se débloque quand gateways, positions et fichiers sont complets',
  VALIDATED: 'VALIDATED',
};

/** Un statut hors DRAFT / VALIDATED (serveur non migré) n'est pas compris : inconnu, jamais vert. */
const unknownStatus = (status: string) => ({
  state: 'unknown' as const,
  detail: `inconnu (statut ${status} non reconnu)`,
});
const isKnownStatus = (status: string): status is SiteConfigStatus => status in STATUS_DETAIL;

export function buildSiteChecklist(input: SiteChecklistInput): ChecklistRow[] {
  const rows: ChecklistRow[] = [];
  const add = (row: Omit<ChecklistRow, 'optional'> & { optional?: boolean }) => rows.push({ optional: false, ...row });

  const gws = input.gateways.status === 'loaded' ? input.gateways.value : null;

  // ── Matériel ──────────────────────────────────────────────────────────────────────────────
  add({
    id: 'gateways',
    group: 'Matériel',
    label: 'Gateways rattachées',
    tab: 'config',
    source: 'gateways du site',
    ...(unavailable(input.gateways) ??
      (gws!.length
        ? { state: 'ok', detail: `${plural(gws!.length, 'gateway')} · ${gws!.map(g => g.name).join(', ')}` }
        : { state: 'todo', detail: 'Aucune gateway sur ce site' })),
  });

  add({
    id: 'positions',
    group: 'Matériel',
    label: 'Antennes positionnées',
    tab: 'config',
    source: 'lat / lon / alt de chaque antenne enregistrée',
    ...(unavailable(input.gateways) ?? positionsRow(gws!)),
  });

  // ── Configuration radio ───────────────────────────────────────────────────────────────────
  add({
    id: 'files',
    group: 'Configuration radio',
    label: 'Fichiers de calibration',
    tab: 'config',
    source: 'fichier de fréquences et calib du site + preset actif',
    ...(unavailable(input.files) ?? filesRow((input.files as { value: ChecklistFiles }).value)),
  });

  const status = input.configStatus.status === 'loaded' ? input.configStatus.value : null;
  add({
    id: 'validated',
    group: 'Configuration radio',
    label: 'Configuration validée',
    tab: 'config',
    source: 'statut de configuration du site',
    ...(unavailable(input.configStatus) ??
      (status == null
        ? { state: 'unknown', detail: 'inconnu (statut absent de la réponse du serveur)' }
        : !isKnownStatus(status)
          ? unknownStatus(status)
          : { state: status === 'VALIDATED' ? 'ok' : 'todo', detail: STATUS_DETAIL[status] })),
  });

  add({
    id: 'applied',
    group: 'Configuration radio',
    label: 'Appliquée par les gateways',
    tab: 'config',
    source: 'aucune donnée : le terrain n’accuse pas réception de la configuration',
    ...(unavailable(input.configStatus) ??
      (status == null
        ? { state: 'unknown', detail: 'inconnu (statut absent de la réponse du serveur)' }
        : !isKnownStatus(status)
          ? unknownStatus(status)
          : status === 'VALIDATED'
            ? {
                state: 'noack',
                detail: 'envoyée — le terrain n’accuse pas réception. Inside ne peut pas savoir si elle est appliquée.',
              }
            : { state: 'later', detail: 'Après la validation' })),
  });

  add({
    id: 'rf',
    group: 'Configuration radio',
    label: 'Émission radio (RF)',
    tab: 'gateways',
    source: 'isEmitting : intention enregistrée, pas une mesure',
    ...(unavailable(input.gateways) ?? rfRow(gws!)),
  });

  // ── Trackers ──────────────────────────────────────────────────────────────────────────────
  const trackers = input.trackers.status === 'loaded' ? input.trackers.value : null;
  add({
    id: 'trackers',
    group: 'Trackers',
    label: 'Trackers assignés',
    tab: 'trackers',
    source: 'trackers du site',
    ...(unavailable(input.trackers) ??
      (trackers! > 0 ? { state: 'ok', detail: plural(trackers!, 'tracker') } : { state: 'todo', detail: '0 tracker' })),
  });

  return rows;
}

function positionsRow(gws: ChecklistGateway[]): { state: ChecklistState; detail: string } {
  if (!gws.length) return { state: 'todo', detail: 'Après les gateways' };
  const all = gws.flatMap(g => antennasOf(g).map((a, i) => ({ g, a, i })));
  const set = all.filter(x => isPositioned(x.a)).length;
  if (set === all.length) return { state: 'ok', detail: `${set} / ${all.length} antennes positionnées` };
  const miss = all.find(x => !isPositioned(x.a))!;
  return {
    state: set > 0 ? 'partial' : 'todo',
    detail: `${set} / ${all.length} antennes positionnées · A${miss.i} de ${miss.g.name} sans position`,
  };
}

function filesRow(f: ChecklistFiles): { state: ChecklistState; detail: string } {
  const kinds: Array<['freq' | 'calib', string]> = [
    ['freq', 'fichier de fréquences'],
    ['calib', 'calib'],
  ];
  const present = kinds.filter(([k]) => f[k]).length;
  const missing = kinds.filter(([k]) => !f[k]).map(([, l]) => l);
  const origin = !f.fromPreset
    ? 'fichiers du site'
    : f.presetName
      ? `preset « ${f.presetName} »`
      : 'preset actif (nom inconnu)';
  if (present === kinds.length) return { state: 'ok', detail: `fichier de fréquences, calib · ${origin}` };
  if (present === 0) return { state: 'todo', detail: 'Aucun fichier (fichier de fréquences, calib)' };
  return { state: 'partial', detail: `${present} / ${kinds.length} · manque ${missing.join(', ')}` };
}

function rfRow(gws: ChecklistGateway[]): { state: ChecklistState; detail: string } {
  if (!gws.length) return { state: 'todo', detail: 'Après les gateways' };
  const on = gws.filter(g => g.isEmitting === true).length;
  const off = gws.filter(g => g.isEmitting === false).map(g => g.name);
  const blank = gws.filter(g => g.isEmitting == null).map(g => g.name);
  const count = `${on} / ${gws.length} en intention d’émettre`;
  if (on === gws.length) {
    return { state: 'intent', detail: `${count} — intention enregistrée, aucune confirmation du terrain` };
  }
  const extra = [
    off.length ? `${off.join(', ')} ${off.length > 1 ? 'coupées' : 'coupée'}` : '',
    blank.length ? `${blank.join(', ')} sans valeur` : '',
  ]
    .filter(Boolean)
    .join(' · ');
  return { state: on > 0 ? 'partial' : 'todo', detail: `${count} · ${extra}` };
}

export function summarizeSiteChecklist(rows: ChecklistRow[]): ChecklistSummary {
  const required = rows.filter(r => !r.optional);
  const count = (...states: ChecklistState[]) => required.filter(r => states.includes(r.state)).length;
  const verified = count('ok');
  const unverifiable = count('noack', 'intent');
  const todoRows = required.filter(r => r.state === 'todo' || r.state === 'partial');
  const unknown = count('unknown');
  const loadingCount = count('loading');
  const next = todoRows[0] ?? null;

  let headline: ChecklistHeadline;
  if (todoRows.length) headline = todoRows.length === 1 && next!.state === 'partial' ? 'almost' : 'not-ready';
  else if (unknown) headline = 'incomplete';
  else if (loadingCount) headline = 'loading';
  else headline = 'reserve';

  return {
    total: required.length,
    passed: verified + unverifiable,
    verified,
    unverifiable,
    todo: todoRows.length,
    later: count('later'),
    unknown,
    loading: loadingCount,
    next,
    headline,
  };
}
